import React, { useState, useMemo } from 'react';
import { ArrowDown, ArrowUp, MapPin } from '../ui/icons';
import {
  PropertyItem,
  PROPERTY_DEPARTMENT_LABELS,
} from '../../models/property';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Pagination,
} from '../ui';

interface PropertiesTableAdvancedProps {
  properties: PropertyItem[];
  onRowClick: (property: PropertyItem) => void;
  sources: { id: string; name: string }[];
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  highlightedPropertyId?: string;
}

type SortField = 'createdAt' | 'title' | 'price' | 'bedrooms' | 'source';
type SortDirection = 'asc' | 'desc';

const PropertiesTableAdvanced: React.FC<PropertiesTableAdvancedProps> = ({
  properties,
  onRowClick,
  sources,
  currentPage,
  pageSize,
  onPageChange,
  highlightedPropertyId = '',
}) => {
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('price');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Create a map of source IDs to names for quick lookup
  const sourceMap = useMemo(() => {
    const map = new Map<string, string>();
    sources.forEach((source) => {
      map.set(source.id, source.name);
    });
    return map;
  }, [sources]);

  // Sort properties
  const sortedProperties = useMemo(() => {
    return [...properties].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'title':
          aValue = a.title || '';
          bValue = b.title || '';
          break;
        case 'price':
          aValue = a.fields.price || 0;
          bValue = b.fields.price || 0;
          break;
        case 'source':
          aValue = sourceMap.get(a.sourceId) || a.sourceId || '';
          bValue = sourceMap.get(b.sourceId) || b.sourceId || '';
          break;
        case 'bedrooms':
          aValue = a.fields.bedrooms || 0;
          bValue = b.fields.bedrooms || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [properties, sortField, sortDirection, sourceMap]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    onPageChange(1);
  };

  const paginatedProperties = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedProperties.slice(startIndex, startIndex + pageSize);
  }, [sortedProperties, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(properties.length / pageSize));

  const formatPrice = (price: number | undefined, currency: string = 'GBP') => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (
    property: PropertyItem
  ): 'error' | 'primary' | 'secondary' | 'default' => {
    const isActive = property.gsi1pk === 'PROPERTY#ACTIVE';
    if (!isActive) return 'error';
    if (property.fields.department === 'residential-sales') return 'primary';
    if (property.fields.department === 'residential-lettings')
      return 'secondary';
    return 'default';
  };

  const getStatusLabel = (property: PropertyItem) => {
    const isActive = property.gsi1pk === 'PROPERTY#ACTIVE';
    if (!isActive) return 'Inactive';
    const department = property.fields.department;
    return department
      ? PROPERTY_DEPARTMENT_LABELS[department] || department
      : 'Unknown';
  };

  const SortableHeader = ({
    field,
    label,
  }: {
    field: SortField;
    label: string;
  }) => (
    <TableHeader className="px-3 py-2">
      <button
        onClick={() => handleSort(field)}
        className="flex items-center gap-1 hover:text-gray-900"
      >
        {label}
        {sortField === field &&
          (sortDirection === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" />
          ))}
      </button>
    </TableHeader>
  );

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <Table>
        <TableHead>
          <TableRow>
            <SortableHeader field="title" label="Property" />
            <SortableHeader field="price" label="Price" />
            <TableHeader className="px-3 py-2">Bedrooms</TableHeader>
            <TableHeader className="px-3 py-2">Department</TableHeader>
            <SortableHeader field="source" label="Source" />
            <SortableHeader field="createdAt" label="Date" />
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedProperties.map((property) => {
            const lat =
              typeof property.fields.latitude === 'number'
                ? property.fields.latitude
                : property.fields.latitude
                ? Number(property.fields.latitude)
                : undefined;
            const lng =
              typeof property.fields.longitude === 'number'
                ? property.fields.longitude
                : property.fields.longitude
                ? Number(property.fields.longitude)
                : undefined;
            const hasCoordinates =
              Number.isFinite(lat ?? NaN) && Number.isFinite(lng ?? NaN);
            const locationText =
              typeof property.fields.location === 'string'
                ? property.fields.location.trim()
                : property.fields.location
                ? String(property.fields.location).trim()
                : '';
            const fallbackLocation = [
              property.fields.city,
              property.fields.state,
            ]
              .filter(Boolean)
              .join(', ');
            const locationDisplay = locationText || fallbackLocation;
            const isHighlightedRow = highlightedPropertyId === property.id;
            const rowClassNames = [
              'hover:bg-gray-50 cursor-pointer',
              isHighlightedRow ? 'ring-2 ring-primary-500 bg-primary-50' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <TableRow
                key={property.id}
                className={rowClassNames}
                onClick={() => onRowClick(property)}
                data-property-highlight={property.id}
              >
                <TableCell className="px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 max-w-[300px] truncate whitespace-nowrap overflow-hidden">
                      {property.title || 'Untitled Property'}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                      <MapPin
                        className={`h-3 w-3 ${
                          hasCoordinates ? 'text-green-600' : 'text-gray-500'
                        }`}
                      />
                      <span>{locationDisplay || 'Location not specified'}</span>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-3 py-2">
                  {formatPrice(property.fields.price, property.fields.currency)}
                </TableCell>
                <TableCell className="px-3 py-2">
                  {property.fields.bedrooms || 'N/A'}
                </TableCell>
                <TableCell className="px-3 py-2">
                  <Badge variant={getStatusColor(property)} size="sm">
                    {getStatusLabel(property)}
                  </Badge>
                </TableCell>
                <TableCell className="px-3 py-2">
                  {sourceMap.get(property.sourceId) || property.sourceId}
                </TableCell>
                <TableCell className="px-3 py-2">
                  {formatDate(property.createdAt)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {properties.length === 0 && (
        <div className="p-12 text-center">
          <p className="text-gray-600">No properties to display</p>
        </div>
      )}
      <div className="border-t bg-white p-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNext={currentPage < totalPages}
          hasPrevious={currentPage > 1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
};

export default PropertiesTableAdvanced;
